"use client";

import { useCallback, useMemo } from "react";
import { APISearchSelect, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui/select/api-search-select";
import type { InsuranceCarrier, InsurancePlan } from "@/types/insurance-carrier";
import { PLAN_TYPE_LABELS } from "@/types/insurance-carrier";
import { listInsuranceCarriers } from "@/api/provider/settings/insurance-carrier";

/**
 * The Insurer field itself — a searchable dropdown over the saved carrier catalog
 * that still accepts anything typed.
 *
 * `APISearchSelect` is API-backed but pick-only ("text that matches no option is
 * never a value"), and a coverage must remain enterable for a carrier nobody has
 * added to the catalog yet. So this composes it WITHOUT editing it, using the same
 * synthetic-option trick as `billing/creatable-code-select.tsx`: the search is
 * wrapped to append a `Use "<typed>"` row, which the list renders as a normal
 * pickable option.
 */

export interface CarrierPlanSelection {
  /** What belongs in the coverage's insurer_name, either way. */
  insurerName: string;
  /** Absent when the user kept their own typed value. */
  carrier?: InsuranceCarrier;
  plan?: InsurancePlan;
}
interface Option extends CarrierPlanSelection {
  id: string;
  /** The synthetic "keep what I typed" row. */
  isFreeText?: boolean;
}

/**
 * Just the address columns, so the carrier editor can compose a preview from its own
 * in-progress form values without first faking a whole InsurancePlan.
 */
export type ClaimsAddressParts = Pick<InsurancePlan, "addressLineOne" | "addressLineTwo" | "city" | "state" | "zipCode">;

/** Compose a plan's structured address into the single-line string a coverage snapshots. */
export function formatPlanClaimsAddress(plan: ClaimsAddressParts): string {
  const street = [plan.addressLineOne, plan.addressLineTwo].filter(part => part?.trim()).join(", ");
  const cityStateZip = [[plan.city, plan.state].filter(part => part?.trim()).join(", "), plan.zipCode?.trim()].filter(Boolean).join(" ");
  return [street, cityStateZip].filter(Boolean).join(", ");
}

/**
 * Whether a claim could actually be put in the post to this plan.
 *
 * Not the same question as formatPlanClaimsAddress returning something: that joins
 * whatever parts exist, so a plan holding only "PO Box 9091" formats to a non-empty
 * string and would otherwise count as done. addressLineTwo stays optional — it is the
 * "Attn:" line, not part of the delivery address.
 */
export function hasMailableClaimsAddress(plan: ClaimsAddressParts): boolean {
  return Boolean(plan.addressLineOne?.trim() && plan.city?.trim() && plan.state?.trim() && plan.zipCode?.trim());
}
function optionId(carrierId: string, planId?: string): string {
  return planId ? `plan:${planId}` : `carrier:${carrierId}`;
}

/** Flatten carriers into one option per plan, plus a carrier-only row when it has none. */
function toOptions(carriers: InsuranceCarrier[]): Option[] {
  return carriers.flatMap(carrier => carrier.plans.length > 0 ? carrier.plans.map(plan => ({
    id: optionId(carrier.id, plan.id),
    insurerName: carrier.name,
    carrier,
    plan
  })) : [{
    id: optionId(carrier.id),
    insurerName: carrier.name,
    carrier
  }]);
}

// Module-level so the identity is stable across renders. APISearchSelect keeps
// formatOption in an effect's dependency list and resets the input from it, so an
// inline arrow would clobber in-progress typing on every keystroke.
//
// `name` is the insurer name alone, because that is what lands in the field once
// picked; the richer "Carrier — Plan" line lives in `optionNode`, which only the
// dropdown row renders.
function formatOption(option: Option) {
  // APISearchSelectItem wraps optionNode in a bare `flex-1`, which cannot shrink
  // below its content — so `truncate` never fires and a long address is chopped
  // mid-character at the popover edge. min-w-0 is what makes it ellipsize.
  const containerClassName = "min-w-0";
  if (option.isFreeText) {
    return {
      id: option.id,
      name: option.insurerName,
      containerClassName,
      optionNode: <>
          <p className="truncate text-body text-foreground">
            {`Use "${option.insurerName}"`}
          </p>
          <p className="truncate text-caption text-muted-foreground">
            Not a saved carrier. Keeps exactly what you typed.
          </p>
        </>
    };
  }
  const {
    carrier,
    plan,
    insurerName
  } = option;
  // Type then claims address, the way Practice Fusion lists plans ("HSA - Choice
  // Plus" over "PO Box 740800 Atlanta GA 30374"). The address earns its place: a
  // carrier can have several plans whose names differ only in where the claim goes.
  const detail = [plan?.planType ? PLAN_TYPE_LABELS[plan.planType] : null, plan ? formatPlanClaimsAddress(plan) || "No claims address" : null, carrier?.code].filter(Boolean).join(" · ") || (plan ? "" : "No plans saved yet");
  return {
    id: option.id,
    name: insurerName,
    containerClassName,
    optionNode:
    // Carrier first and dominant, plan second: consecutive rows for the same
    // carrier then read as a group without a dash gluing the two names into
    // one run-on label.
    <>
        <p className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-body-strong text-foreground">
            {insurerName}
          </span>
          {plan && <span className="truncate text-body text-foreground-secondary">
              {plan.name}
            </span>}
        </p>
        {detail && <p className="truncate text-caption text-muted-foreground">
            {detail}
          </p>}
      </>
  };
}
interface CarrierPlanSelectProps {
  /** The coverage's current insurer name — free text or a catalog carrier. */
  value: string;
  /** Set when the coverage is linked to a catalog plan, so the row shows as selected. */
  planId?: string | null;
  onSelect: (selection: CarrierPlanSelection) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  dataTestId?: string;
}
export default function CarrierPlanSelect({
  value,
  planId,
  onSelect,
  disabled,
  hasError,
  // Short enough to survive a half-width field; the longer sentence used to
  // truncate mid-word in the trigger itself.
  placeholder = "Search or type a carrier",
  dataTestId
}: CarrierPlanSelectProps) {
  const handleSearch = useCallback(async (search: string) => {
    // Swallowed so a carrier stays typeable when the lookup is down.
    let carriers: InsuranceCarrier[] | null = null;
    try {
      carriers = await listInsuranceCarriers(search || undefined);
    } catch {
      carriers = [];
    }
    if (carriers === null) return null;
    const options = toOptions(carriers);
    const typed = search.trim();
    if (!typed) return options;
    // Catalog matches lead; the escape hatch sits underneath them so it never
    // steals the obvious pick, but is always reachable.
    return [...options, {
      id: `free:${typed}`,
      insurerName: typed,
      isFreeText: true
    }];
  }, []);

  // Keyed on stable primitives: a fresh object each render would fight
  // APISearchSelect's controlled-value effect and overwrite what's being typed.
  const selected = useMemo<Option | undefined>(() => value ? {
    id: planId ? `plan:${planId}` : `free:${value}`,
    insurerName: value
  } : undefined, [value, planId]);
  return <APISearchSelect<Option> value={selected} onValueChange={picked => {
    if (!picked) return;
    onSelect({
      insurerName: picked.insurerName,
      carrier: picked.isFreeText ? undefined : picked.carrier,
      plan: picked.isFreeText ? undefined : picked.plan
    });
  }} onSearch={handleSearch} formatOption={formatOption}>
      <APISearchSelectTrigger placeholder={placeholder} disabled={disabled} hasError={hasError} openOnFocus dataTestId={dataTestId} ariaLabel="Insurer" />
      <APISearchSelectContent emptyMessage="No saved carriers match. Keep typing to use your own value." optionTestIdPrefix="carrier-plan-option" />
    </APISearchSelect>;
}